/**
 * The browser's Back with unsaved edits: asks instead of leaving (QA round 1, EX-02: Back left the editor and lost
 * the edits; a reload already asks through `beforeunload`).
 *
 * The dashboard uses a plain BrowserRouter (no navigation blocker), so while `active` the hook keeps one extra history
 * entry of the same address on top (marked in its state). Back lands on the editor's own entry: nothing changes on
 * screen, the hook puts the extra entry back and calls `onBack` (the editor's question). `leave()` then really goes
 * back; when the edits are saved (`active` false) the extra entry is taken away again.
 */
import { useCallback, useEffect, useRef } from "react";

const MARK = "solarBackGuard";

type HistoryLike = Pick<History, "state" | "pushState" | "back" | "go">;

const marked = (state: unknown) => !!(state && typeof state === "object" && (state as Record<string, unknown>)[MARK]);

/** Puts the guard entry on top (a copy of the current state, marked), unless it is there already. */
export function armGuard(h: HistoryLike): void {
  if (marked(h.state)) return;
  h.pushState({ ...((h.state as object | null) ?? {}), [MARK]: true }, "");
}

/** A popstate while guarded: true when it was Back off the guard entry (ask), and then the entry is back on top. */
export function onGuardPop(h: HistoryLike): boolean {
  if (marked(h.state)) return false; // Forward onto the guard entry again
  armGuard(h);
  return true;
}

export function useBackGuard(active: boolean, onBack: () => void): { leave: () => void } {
  const ask = useRef(onBack);
  ask.current = onBack;
  const leaving = useRef(false);
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const h = window.history;
    armGuard(h);
    const pop = () => {
      if (!leaving.current && onGuardPop(h)) ask.current();
    };
    window.addEventListener("popstate", pop);
    return () => {
      window.removeEventListener("popstate", pop);
      // saved while still here: take the extra entry away (leaving goes back past it by itself)
      if (!leaving.current && marked(h.state)) h.back();
    };
  }, [active]);
  /** Really go back: past the guard entry and the editor's own. */
  const leave = useCallback(() => {
    leaving.current = true;
    window.history.go(marked(window.history.state) ? -2 : -1);
  }, []);
  return { leave };
}
